import { useEffect, useState, useCallback, useRef } from "react";
import { IllustrationPages } from "../components/illustrations.jsx";

export default function Playbook() {
  const [cards, setCards] = useState(null);
  const [index, setIndex] = useState(0);
  const [showContents, setShowContents] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    fetch("/api/content/playbook", { credentials: "include" })
      .then((r) => r.json())
      .then(setCards);
  }, []);

  const handleKey = useCallback(
    (e) => {
      if (!cards) return;
      if (e.key === "Escape") { setShowContents(false); return; }
      if (showContents) return;
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndex((i) => Math.min(cards.length - 1, i + 1));
    },
    [cards, showContents]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const jumpTo = useCallback((i) => {
    setIndex(i);
    setShowContents(false);
  }, []);

  if (!cards) return null;
  const card = cards[index];

  return (
    <div className="page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div className="kicker">
          <span className="the-line" />
          THE PLAYBOOK
        </div>
        <button
          className="btn-secondary"
          onClick={() => setShowContents(true)}
          style={{ fontSize: 12, padding: "4px 12px", flexShrink: 0 }}
          aria-label="Open card contents list"
        >
          Contents
        </button>
      </div>

      <h1 style={{ marginBottom: 24 }}>The method, plainly</h1>

      <div className="card" style={{ minHeight: 240, display: "flex", flexDirection: "column", position: "relative" }}>
        <div className="kicker" style={{ marginBottom: 8 }}>
          <span className="the-line" />
          {card.kicker}
        </div>
        <div className="card-title" style={{ marginBottom: 16 }}>{card.title}</div>
        <IllustrationPages className="illustration-card-accent" />
        <p style={{ flex: 1, marginBottom: 20 }}>{card.body}</p>
        <p style={{ borderTop: "1px solid var(--hairline)", paddingTop: 16, fontWeight: 600 }}>{card.takeaway}</p>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
        <button className="btn-secondary" onClick={() => setIndex(Math.max(0, index - 1))} disabled={index === 0}>
          Previous
        </button>
        <div className="progress-dots">
          {cards.map((_, i) => (
            <span
              key={i}
              className={i === index ? "active" : ""}
              onClick={() => setIndex(i)}
              role="button"
              aria-label={`Go to card ${i + 1}`}
              style={{ cursor: "pointer" }}
            />
          ))}
        </div>
        <button
          className="btn-secondary"
          onClick={() => setIndex(Math.min(cards.length - 1, index + 1))}
          disabled={index === cards.length - 1}
        >
          Next
        </button>
      </div>

      {showContents && (
        <div
          onClick={() => setShowContents(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,18,16,0.7)",
            zIndex: 200,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(340px, 92vw)",
              height: "100%",
              background: "var(--surface, #171B18)",
              borderLeft: "1px solid var(--hairline)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 20px 16px",
              borderBottom: "1px solid var(--hairline)",
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--bronze-lit)" }}>
                Contents
              </span>
              <button
                className="btn-secondary"
                onClick={() => setShowContents(false)}
                style={{ fontSize: 12, padding: "3px 10px" }}
                aria-label="Close contents"
              >
                Close
              </button>
            </div>
            <ol style={{
              listStyle: "none",
              margin: 0,
              padding: "8px 0",
              overflowY: "auto",
              flex: 1,
            }}>
              {cards.map((c, i) => (
                <li key={i}>
                  <button
                    onClick={() => jumpTo(i)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      background: i === index ? "var(--raised, #20261F)" : "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--hairline)",
                      padding: "13px 20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "baseline",
                      gap: 12,
                      color: "var(--bone)",
                    }}
                  >
                    <span style={{
                      flexShrink: 0,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 11,
                      color: "var(--muted, #8B928B)",
                      minWidth: 20,
                    }}>
                      {i + 1}
                    </span>
                    <span style={{ fontSize: 14, lineHeight: 1.4 }}>{c.title}</span>
                    {i === index && (
                      <span style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--bronze)", flexShrink: 0 }} />
                    )}
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
